import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import OtterIcon from "@/components/app/OtterIcon";
import {
  Radar, Mail, Loader2, ArrowUpRight, TrendingUp,
} from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const m = Math.floor(Math.max(0, Date.now() - then) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

/**
 * Admin-only Opportunity Radar — weekly editorial planner.
 * Renders the top 5 highest-opportunity keywords across admin projects
 * that don't yet have a published or pending post.
 *
 * Hidden entirely for non-admins.
 */
export default function OpportunityRadar({ user }) {
  const isAdmin = user && typeof user === "object" && user.role === "admin";
  const [data, setData] = useState(null);
  const [running, setRunning] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get("/admin/opportunity-radar");
      setData(data);
    } catch {
      setData({ opportunities: [], count: 0, last_run_at: null });
    }
  };

  useEffect(() => {
    if (isAdmin) refresh();
  }, [isAdmin]);

  if (!isAdmin || data === null) return null;
  const list = data.opportunities || [];
  if (list.length === 0 && !data.last_run_at) return null;

  async function emailNow() {
    setRunning(true);
    try {
      const { data: r } = await api.post("/admin/opportunity-radar/run");
      if (r.reason === "sent") {
        toast.success(`Radar emailed to ${r.emails_sent} admin${r.emails_sent === 1 ? "" : "s"}`, {
          description: `${r.count} opportunit${r.count === 1 ? "y" : "ies"} included.`,
        });
      } else if (r.reason === "rate_limited") {
        toast.message("Already sent this week", { description: "Radar respects a 5-day floor between runs." });
      } else if (r.reason === "no_recipients" || r.reason === "email_disabled") {
        toast.message("Email skipped", { description: "No verified admin recipients (or Resend not configured) — the in-app preview is still up-to-date." });
      } else if (r.reason === "no_opportunities") {
        toast.message("Nothing to ship right now", { description: "Track more high-volume / low-difficulty keywords." });
      }
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="mb-8 border border-foreground bg-white" data-testid="opportunity-radar">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 p-4 border-b border-border">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-8 h-8 bg-foreground grid place-items-center text-background flex-shrink-0">
            <Radar className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <div className="font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground">
              // opportunity radar · admin only
            </div>
            <div className="text-sm font-medium mt-0.5">
              {list.length > 0
                ? `Top ${list.length} keyword${list.length === 1 ? "" : "s"} to ship next`
                : "No opportunities right now"}
              {data.last_run_at && (
                <span className="ml-2 text-muted-foreground">· emailed {timeAgo(data.last_run_at)}</span>
              )}
            </div>
          </div>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="rounded-none"
          onClick={emailNow}
          disabled={running}
          data-testid="opportunity-radar-run-now"
        >
          {running ? (
            <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
          ) : (
            <Mail className="w-3.5 h-3.5 mr-1.5" />
          )}
          Email me now
        </Button>
      </div>

      {list.length > 0 ? (
        <ol className="divide-y divide-border" data-testid="opportunity-radar-list">
          {list.map((o, idx) => (
            <li key={`${o.project_id}:${o.keyword}`} className="p-4 flex items-center gap-4" data-testid={`opportunity-radar-row-${idx}`}>
              <div className="font-mono-data text-xs text-muted-foreground w-6 flex-shrink-0">#{idx + 1}</div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold tracking-tight text-base truncate">{o.keyword}</div>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  <span>{o.project_name}</span>
                  {o.intent && (
                    <span className="font-mono-data uppercase tracking-widest text-[10px]">· {o.intent}</span>
                  )}
                </div>
              </div>
              <div className="hidden sm:flex items-center gap-4 font-mono-data text-xs text-foreground/80 flex-shrink-0">
                <div className="text-right">
                  <div>{Number(o.volume).toLocaleString()}/mo</div>
                  <div className="text-muted-foreground">vol</div>
                </div>
                <div className="text-right">
                  <div>{o.difficulty}/100</div>
                  <div className="text-muted-foreground">diff</div>
                </div>
                <div className="text-right">
                  <div className="text-emerald-600 inline-flex items-center gap-1">
                    <TrendingUp className="w-3 h-3" /> {o.score}
                  </div>
                  <div className="text-muted-foreground">score</div>
                </div>
              </div>
              <Button
                asChild
                size="sm"
                variant="ghost"
                className="rounded-none flex-shrink-0"
                data-testid={`opportunity-radar-open-${idx}`}
              >
                <Link to={`/app/projects/${o.project_id}?tab=content`}>
                  Open <ArrowUpRight className="w-3.5 h-3.5 ml-1" />
                </Link>
              </Button>
            </li>
          ))}
        </ol>
      ) : (
        <div className="p-6 text-sm text-muted-foreground flex items-center gap-3" data-testid="opportunity-radar-empty">
          <OtterIcon size={28} className="text-foreground" />
          <div>
            Arlo couldn&apos;t find new opportunities — every high-value keyword is already published or queued.
            Add more tracked keywords to surface fresh wins.
          </div>
        </div>
      )}
    </div>
  );
}
